import { useAttachmentListQuery } from 'api';
import { useCallback, useState } from 'react';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { MicroserviceTypes, OrderType, PaginationType, SortParamsType } from 'typings';
import { AttachmentDataType } from './index';
import { Form, TablePaginationConfig, TableProps } from 'components/index';
import { dateFormatter } from 'constants/index';

export interface IAttachmentFilterParams {
  attachmentTypes?: string;
  createdDate?: any;
  updatedDate?: any;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useAttachments = ({
  entityName,
  entityId,
  service,
}: {
  entityName: string;
  entityId: string;
  service?: MicroserviceTypes;
}) => {
  const [filtersForm] = Form.useForm();
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [filterDrawerState, setFilterDrawerState] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [filters, setFilters] = useState<IAttachmentFilterParams>({});
  const [deleteItem, setDeleteItem] = useState<Record<string, any> | undefined>();
  const [dataToEdit, setDataToEdit] = useState<AttachmentDataType | undefined>(undefined);

  const { data = {}, isLoading } = useAttachmentListQuery({
    entityName,
    entityId,
    service,
    page: pagination.page,
    size: pagination.size,
    sortField: sort.field,
    sortOrder: sort.order,
    filters,
  });

  const onOpenFilters = useCallback(() => setFilterDrawerState(true), []);
  const onCloseFilters = useCallback(() => setFilterDrawerState(false), []);

  const applyFilters = useCallback(
    (values: IAttachmentFilterParams) => {
      const _filters: any = {
        ...(values.attachmentTypes && { attachmentTypes: values.attachmentTypes }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.updatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.updatedDate[0], 'start'),
        }),
        ...(values.updatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.updatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );
  const resetFilters = useCallback(() => setFilters({}), []);

  const handleTableChange: TableProps<AttachmentDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<AttachmentDataType> | SorterResult<AttachmentDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onHandleDelete = useCallback((value: any | undefined) => {
    setDeleteItem(value);
  }, []);

  const onDeleteModalClose = useCallback(() => {
    setDeleteItem(undefined);
  }, []);

  const onAddModalStateChange = useCallback((value?: AttachmentDataType) => {
    setDataToEdit(value || undefined);
    setOpenAddModal((oState) => !oState);
  }, []);

  return {
    handleTableChange,
    attachmentList: data?.content || [],
    total: data.totalElements ?? 0,
    pagination,
    isLoading,
    onHandleDelete,
    onDeleteModalClose,
    deleteItem,
    dataToEdit,
    openAddModal,
    onAddModalStateChange,
    applyFilters,
    resetFilters,
    filtersForm,
    filterDrawerState,
    onCloseFilters,
    onOpenFilters,
  };
};
